import type { ComponentPropsWithoutRef, ElementType, ReactElement, ReactNode } from "react";

import { cn } from "@/lib/utils";

/** Visual scale for document and UI headings. */
export type HeadingVariant =
  | "page"
  | "chrome"
  | "section"
  | "subsection"
  | "drawer"
  | "formPage"
  | "card"
  | "panel"
  | "panelStrong"
  | "item";

export const headingVariantClassName: Record<HeadingVariant, string> = {
  page: "font-heading text-2xl font-semibold tracking-tight text-foreground",
  chrome: "font-heading text-base font-semibold tracking-tight text-foreground",
  section: "font-heading text-lg font-semibold tracking-tight text-foreground",
  subsection: "font-heading text-base font-semibold tracking-tight text-foreground",
  drawer: "font-heading text-base font-medium text-foreground",
  formPage: "font-heading text-2xl font-semibold tracking-tight text-foreground sm:text-3xl",
  card: "font-heading text-base leading-snug font-medium text-foreground group-data-[size=sm]/card:text-sm",
  panel: "text-sm font-medium text-foreground",
  panelStrong: "font-heading text-sm font-semibold text-foreground",
  item: "min-w-0 truncate text-sm font-medium text-foreground",
};

const defaultHeadingLevel: Record<HeadingVariant, 1 | 2 | 3 | 4 | 5 | 6> = {
  page: 1,
  chrome: 1,
  section: 2,
  subsection: 3,
  drawer: 2,
  formPage: 1,
  card: 3,
  panel: 2,
  panelStrong: 2,
  item: 4,
};

type HeadingProps = {
  variant: HeadingVariant;
  /** Semantic heading level. Defaults per variant (page/chrome → h1, section → h2, etc.). */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Override the rendered element (e.g. span in chrome bars). */
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<"h1">, "children" | "className">;

export function Heading({
  variant,
  level,
  as,
  className,
  children,
  ...props
}: HeadingProps): ReactElement {
  const resolvedLevel = level ?? defaultHeadingLevel[variant];
  const Tag = as ?? (`h${resolvedLevel}` as ElementType);

  return (
    <Tag
      data-slot="heading"
      data-variant={variant}
      className={cn(headingVariantClassName[variant], className)}
      {...props}
    >
      {children}
    </Tag>
  );
}

export type StatValueSize = "md" | "lg";

export const statValueClassName: Record<StatValueSize, string> = {
  md: "font-heading text-2xl font-semibold tabular-nums tracking-tight text-foreground",
  lg: "font-heading text-3xl font-semibold tabular-nums tracking-tight text-foreground",
};

type StatValueProps = {
  size?: StatValueSize;
  as?: ElementType;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<"span">, "children" | "className">;

/** Large numeric display — not a document heading. */
export function StatValue({
  size = "md",
  as: Tag = "span",
  className,
  children,
  ...props
}: StatValueProps): ReactElement {
  return (
    <Tag
      data-slot="stat-value"
      data-size={size}
      className={cn(statValueClassName[size], className)}
      {...props}
    >
      {children}
    </Tag>
  );
}

export type FieldLabelVariant = "detail" | "strong" | "muted" | "overline";

export const fieldLabelVariantClassName: Record<FieldLabelVariant, string> = {
  /** Canonical read-only field label (drawers, detail pages, review summaries). */
  detail: "text-sm font-medium text-muted-foreground",
  /** Compact strong label for emphasis inside dense layouts. */
  strong: "text-xs font-semibold tracking-wide text-foreground",
  /** Compact secondary label — timestamps, tertiary metadata. */
  muted: "text-xs font-medium tracking-wide text-muted-foreground",
  overline: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
};

/** Read-only field labels. */
export const detailLabelClassName = fieldLabelVariantClassName.detail;

/** Read-only primary value text. */
export const detailValueClassName = "text-sm text-foreground";

/** Read-only scalar value text (names, counts, short answers). */
export const detailValueEmphasisClassName = "text-sm font-medium text-foreground";

/** Body / description text on nested read-only cards (readable on bordered surfaces). */
export const detailSecondaryTextClassName = "text-sm text-foreground/80";

/** Timestamps and tertiary metadata on main surfaces — same typeface and weight as other values. */
export const detailTertiaryTextClassName = "text-sm font-medium text-foreground";

/** Read-only de-emphasized value text (secondary dates in detail fields). */
export const detailValueMutedClassName = "text-sm text-foreground/65";

type FieldLabelElement = "span" | "dt" | "label" | "p" | "div" | "legend";

type FieldLabelProps = {
  variant?: FieldLabelVariant;
  as?: FieldLabelElement;
  className?: string;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<"span">, "children" | "className">;

/** Field / metadata labels — not document headings (never h1–h6). */
export function FieldLabel({
  variant = "detail",
  as: Tag = "span",
  className,
  children,
  ...props
}: FieldLabelProps): ReactElement {
  return (
    <Tag
      data-slot="field-label"
      data-variant={variant}
      className={cn(fieldLabelVariantClassName[variant], className)}
      {...props}
    >
      {children}
    </Tag>
  );
}
